import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.bundle.min';
import './App.css';
import './Home.css';
import Header from './header/Header';
import Footer from './footer/Footer';
import DishInput from './recipe/DishInput';
import IngredientInput from './recipe/IngredientInput';
import DietControl from './recipe/DietControl';
import AllRecipes from './recipe/AllRecipes';
import Recipe from './recipe/Recipe';
import Marketplace from './marketplace/Marketplace';
import LoginSuccess from './LoginSuccess';
import api, { redirectToLogin } from './api';
import Seo from './Seo';

const FEATURE_CARDS = [
  {
    id: 'dish',
    title: 'I know what to cook',
    description: 'Find the recipe for a dish you already love.',
    image: 'photo-1565557623262-b51c2513a641',
    imageAlt: 'A freshly prepared Indian curry',
  },
  {
    id: 'ingredient',
    title: 'Discover from Ingredients',
    description: 'Make something delicious with what you have.',
    image: 'photo-1547592180-85f173990554',
    imageAlt: 'Fresh colourful ingredients ready to cook',
  },
  {
    id: 'diet',
    title: 'Personal Diet plan',
    description: 'Get a meal plan tailored to your goals.',
    image: 'photo-1512621776951-a57141f2eefd',
    imageAlt: 'A nourishing bowl of fresh food',
  },
];

/* ── Home ─────────────────────────────────────────── */
function Home() {
  const navigate = useNavigate();

  const handleCardClick = (id) => {
    if (id === 'diet') {
      navigate('/diet');
    } else if (id === 'dish') {
      navigate('/dish');
    } else {
      navigate('/ingredients');
    }
  };

  return (
    <main className="home-page premium-home">
      <section className="premium-home-collection" id="collections" aria-labelledby="collection-title">
        <div className="premium-home-collection-inner">
          <div className="premium-home-section-heading">
            <span className="premium-home-overline">INDIAN RECIPES · EVERYDAY FLAVOUR · WELLNESS</span>
            <span className="premium-home-flourish" aria-hidden="true">✦ &nbsp; ✦ &nbsp; ✦</span>
            <h1 id="collection-title">Indian flavours,<br /><em>made for your table.</em></h1>
            <p>Find a much-loved recipe, cook with what’s in your kitchen, or shape a meal plan around your wellbeing.</p>
          </div>
          <div className="premium-home-collection-search">
            <span aria-hidden="true">✧</span>
            <span>Something delicious is waiting</span>
            <button type="button" onClick={() => navigate('/recipes')}>Explore recipes <span aria-hidden="true">→</span></button>
          </div>
          <div className="path-grid" aria-label="Choose how to discover Indian food">
            {FEATURE_CARDS.map((card, index) => (
              <button
                key={card.id}
                type="button"
                className={`path-card path-card-${card.id}`}
                onClick={() => handleCardClick(card.id)}
                style={{ '--card-index': index }}
              >
                <span className="path-card-copy">
                  <span className="path-card-title">{card.title}</span>
                  <span className="path-card-description">{card.description}</span>
                </span>
                <span className="premium-home-card-visual">
                  <img
                    src={`https://images.unsplash.com/${card.image}?auto=format&fit=crop&w=240&q=78`}
                    alt={card.imageAlt}
                    loading="lazy"
                  />
                  <span className="path-card-arrow" aria-hidden="true">→</span>
                </span>
              </button>
            ))}
          </div>
          <p className="premium-home-collection-note"><span aria-hidden="true">✦</span> Rooted in tradition. Made for your table. <span aria-hidden="true">✦</span></p>
        </div>
      </section>
    </main>
  );
}

/* ── Page wrappers — <main> ensures flex:1 keeps footer pinned ── */
function DishPage() {
  const navigate = useNavigate();
  return (
    <>
      <Seo
        title="Find an Indian Dish Recipe | Indian Food Delight"
        description="Search for a favourite Indian dish and discover recipes with ingredients, preparation steps, and cooking tips."
        path="/dish"
      />
      <main><DishInput onBack={() => navigate(-1)} /></main>
    </>
  );
}

function IngredientsPage() {
  const navigate = useNavigate();
  return (
    <>
      <Seo
        title="Recipes by Ingredients | Indian Food Delight"
        description="Find Indian recipes to make with the ingredients already in your kitchen."
        path="/ingredients"
      />
      <main><IngredientInput onBack={() => navigate(-1)} /></main>
    </>
  );
}

function DietPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileError, setProfileError] = useState('');

  useEffect(() => {
    let isActive = true;
    const returnTo = `${location.pathname}${location.search}`;

    if (!localStorage.getItem('authToken')) {
      redirectToLogin(returnTo);
      return () => { isActive = false; };
    }

    setLoading(true);
    setProfileError('');
    api.get('/auth/user')
      .then((response) => {
        if (isActive) setUser(response.data);
      })
      .catch(() => {
        if (isActive) setProfileError('We couldn’t verify your sign-in. Please try again.');
      })
      .finally(() => {
        if (isActive) setLoading(false);
      });

    return () => { isActive = false; };
  }, [location.pathname, location.search]);

  if (loading) {
    return (
      <>
        <Seo
          title="Personal Meal Plans | Indian Food Delight"
          description="Create and manage a personalized meal plan with Indian Food Delight."
          path="/diet"
          robots="noindex,follow"
        />
        <main className="content-panel-wide" role="status" aria-live="polite">
          Verifying your sign-in…
        </main>
      </>
    );
  }

  if (profileError) {
    return (
      <>
        <Seo
          title="Personal Meal Plans | Indian Food Delight"
          description="Create and manage a personalized meal plan with Indian Food Delight."
          path="/diet"
          robots="noindex,follow"
        />
        <main className="content-panel-wide">
          <p role="alert">{profileError}</p>
          <button type="button" onClick={() => window.location.reload()}>Try again</button>
        </main>
      </>
    );
  }

  return (
    <>
      <Seo
        title="Personal Meal Plans | Indian Food Delight"
        description="Create and manage a personalized meal plan with Indian Food Delight."
        path="/diet"
        robots="noindex,follow"
      />
      <main>
        <DietControl
          onBack={() => navigate(-1)}
          userName={user?.name || ''}
          initialView={new URLSearchParams(location.search).get('view') === 'all'}
          onViewAllPlans={() => navigate('/diet?view=all')}
        />
      </main>
    </>
  );
}

/* ── App ──────────────────────────────────────────── */
function App() {
  const location = useLocation();
  const hideHeaderFooter = location.pathname === '/';

  return (
    <div id="root" className="luxury-app">
      {!hideHeaderFooter && <Header />}
      <Routes>
        <Route
          path="/"
          element={(
            <>
              <Seo
                title="Indian Food Delight | Indian Recipes & Personalized Diet Plans"
                description="Discover delicious Indian recipes, cook with ingredients you have, and explore personalized meal plans with Indian Food Delight."
                path="/"
              />
              <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                  __html: JSON.stringify({
                    '@context': 'https://schema.org',
                    '@type': 'WebSite',
                    name: 'Indian Food Delight',
                    url: 'https://indianfooddelight.com/',
                  }).replace(/</g, '\\u003c'),
                }}
              />
              <Home />
            </>
          )}
        />
        <Route path="/dish"          element={<DishPage />} />
        <Route path="/ingredients"   element={<IngredientsPage />} />
        <Route path="/diet"          element={<DietPage />} />
        <Route path="/recipes" element={<AllRecipes />} />
        <Route path="/recipes/:slug" element={<Recipe />} />
        <Route
          path="/marketplace"
          element={(
            <>
              <Seo
                title="Kitchen Favourites | Indian Food Delight Marketplace"
                description="Explore kitchen tools and thoughtful finds selected for joyful Indian cooking."
                path="/marketplace"
              />
              <Marketplace />
            </>
          )}
        />
        <Route
          path="/login-success"
          element={(
            <>
              <Seo
                title="Completing Sign In | Indian Food Delight"
                description="Complete your sign-in to Indian Food Delight."
                path="/login-success"
                robots="noindex,follow"
              />
              <LoginSuccess />
            </>
          )}
        />
        <Route
          path="*"
          element={(
            <main className="content-panel" style={{ padding: '24px' }}>
              <Seo
                title="Page Not Found | Indian Food Delight"
                description="The requested page could not be found."
                path={location.pathname}
                robots="noindex,follow"
              />
              <h1>Page Not Found</h1>
              <p>The page you requested could not be found.</p>
            </main>
          )}
        />
      </Routes>
      {!hideHeaderFooter && <Footer />}
    </div>
  );
}

function AppRouter() {
  return (
    <HelmetProvider>
      <Router>
        <App />
      </Router>
    </HelmetProvider>
  );
}

export default AppRouter;
